import { useState } from 'react';

import {
  identityApiRef,
  useApi,
  discoveryApiRef,
  alertApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  UpdateOwnershipItem,
  UpdateOwnershipRequest,
  UpdateOwnershipResponse,
} from '../../../types';
import { OwnershipEntity, OwnershipHandlerResult } from '../types';

export interface RepositoryOwnershipHandlerResult
  extends OwnershipHandlerResult {
  markAsUnknown: (entities: OwnershipEntity[]) => Promise<boolean>;
}

interface UserProfile {
  email?: string;
}

function extractRepoName(githubProjectSlug: string): string {
  return githubProjectSlug.split('/')[1] || githubProjectSlug;
}

async function updateRepositoryOwnership(
  discoveryApi: DiscoveryApi,
  request: UpdateOwnershipRequest,
): Promise<UpdateOwnershipResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('engineering-insights');

  const response = await fetch(`${baseUrl}/api/v1/repos/ownership/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update ownership: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

async function refreshRepositories(
  discoveryApi: DiscoveryApi,
  repositoryNames: string[],
): Promise<{ updatedCount: number }> {
  const baseUrl = await discoveryApi.getBaseUrl('utility-api');

  const response = await fetch(`${baseUrl}/actions/refresh-repo-ownership`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ repository_names: repositoryNames }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to refresh repositories: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

/**
 * Hook for handling Repository ownership via engineering-insights API.
 */
export function useRepositoryOwnershipHandler(): RepositoryOwnershipHandlerResult {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const alertApi = useApi(alertApiRef);
  const [loading, setLoading] = useState(false);

  const submit = async (
    entities: OwnershipEntity[],
    ownerRef: string,
  ): Promise<boolean> => {
    setLoading(true);
    try {
      // Get user email
      const identity = await identityApi.getBackstageIdentity();
      const userEntity = await catalogApi.getEntityByRef(
        identity.userEntityRef,
      );
      const userEmail =
        (userEntity?.spec?.profile as UserProfile)?.email || 'unknown';

      // Normalize team entity ref
      const fullTeamEntityRef = ownerRef.includes(':')
        ? ownerRef
        : `group:default/${ownerRef}`;

      // Fetch the selected team entity
      const teamEntity = await catalogApi.getEntityByRef(fullTeamEntityRef);

      if (!teamEntity) {
        alertApi.post({
          message: `Team entity not found: ${fullTeamEntityRef}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      // Extract team ID and details
      const ownerTeamId =
        fullTeamEntityRef.split('/').pop()?.toUpperCase() || '';
      const teamAnnotations = teamEntity.metadata.annotations || {};
      const teamName = teamEntity.metadata.title || teamEntity.metadata.name;

      // Validate required fields
      const manager = teamAnnotations['graph.microsoft.com/group-manager'];
      const costCenter =
        teamAnnotations['graph.microsoft.com/cost-center-name'];
      const costCenterId =
        teamAnnotations['graph.microsoft.com/cost-center-id'];
      const ownerOu =
        teamAnnotations['graph.microsoft.com/organization-unit-name'];
      const ownerBu = teamAnnotations['graph.microsoft.com/business-unit-name'];

      const missingFields: string[] = [];
      if (!manager)
        missingFields.push('manager (graph.microsoft.com/group-manager)');
      if (!costCenter)
        missingFields.push(
          'cost center (graph.microsoft.com/cost-center-name)',
        );
      if (!ownerOu)
        missingFields.push(
          'organization unit (graph.microsoft.com/organization-unit-name)',
        );
      if (!ownerBu)
        missingFields.push(
          'business unit (graph.microsoft.com/business-unit-name)',
        );

      if (missingFields.length > 0) {
        alertApi.post({
          message: `Cannot accept ownership: Team "${teamName}" is missing required fields: ${missingFields.join(
            ', ',
          )}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      // Build update requests for all entities
      const updates: UpdateOwnershipItem[] = [];
      const repositoryNames: string[] = [];

      for (const entity of entities) {
        const githubSlug = entity.annotations?.['github.com/project-slug'];

        if (!githubSlug) {
          alertApi.post({
            message: `Repository ${entity.name} is missing github.com/project-slug annotation`,
            severity: 'error',
          });
          setLoading(false);
          return false;
        }

        const repositoryName = extractRepoName(githubSlug);
        if (!repositoryName) {
          alertApi.post({
            message: `Could not extract repository name from ${githubSlug}`,
            severity: 'error',
          });
          setLoading(false);
          return false;
        }

        repositoryNames.push(repositoryName);

        updates.push({
          repository_name: repositoryName,
          owner_confirm: true,
          owner_team_id: ownerTeamId,
          owner_team_name: teamName,
          owner_manager: manager,
          owner_cost_center: costCenter,
          owner_cost_center_id: costCenterId || undefined,
          owner_ou: ownerOu,
          owner_bu: ownerBu,
          updated_by: userEmail,
        });
      }

      // Update ownership via API
      const updateResult = await updateRepositoryOwnership(discoveryApi, {
        updates,
      });

      if (updateResult.failed.length > 0) {
        alertApi.post({
          message: `Failed to update ownership for ${
            updateResult.failed.length
          } repositories: ${updateResult.failed.map(f => f.error).join(', ')}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      // Refresh repository entities
      await refreshRepositories(discoveryApi, repositoryNames);

      const message =
        entities.length === 1
          ? `Successfully assigned ownership to ${teamName}`
          : `Successfully assigned ownership for ${entities.length} repositories to ${teamName}`;

      alertApi.post({
        message,
        severity: 'success',
        display: 'transient',
      });

      setLoading(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      alertApi.post({
        message: `Failed to accept ownership: ${errorMessage}`,
        severity: 'error',
      });
      setLoading(false);
      return false;
    }
  };

  const markAsUnknown = async (
    entities: OwnershipEntity[],
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const identity = await identityApi.getBackstageIdentity();
      const userEntity = await catalogApi.getEntityByRef(
        identity.userEntityRef,
      );
      const userEmail =
        (userEntity?.spec?.profile as UserProfile)?.email || 'unknown';

      const updates: UpdateOwnershipItem[] = [];
      const repositoryNames: string[] = [];

      for (const entity of entities) {
        const githubSlug = entity.annotations?.['github.com/project-slug'];

        if (!githubSlug) {
          alertApi.post({
            message: `Repository ${entity.name} is missing github.com/project-slug annotation`,
            severity: 'error',
          });
          setLoading(false);
          return false;
        }

        const repositoryName = extractRepoName(githubSlug);
        if (!repositoryName) {
          alertApi.post({
            message: `Could not extract repository name from ${githubSlug}`,
            severity: 'error',
          });
          setLoading(false);
          return false;
        }

        repositoryNames.push(repositoryName);
        updates.push({
          repository_name: repositoryName,
          owner_confirm: false,
          updated_by: userEmail,
        });
      }

      const updateResult = await updateRepositoryOwnership(discoveryApi, {
        updates,
      });

      if (updateResult.failed.length > 0) {
        alertApi.post({
          message: `Failed to unconfirm ownership for ${
            updateResult.failed.length
          } repositories: ${updateResult.failed.map(f => f.error).join(', ')}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      await refreshRepositories(discoveryApi, repositoryNames);

      const message =
        entities.length === 1
          ? 'Successfully marked repository ownership as unconfirmed'
          : `Successfully marked ${entities.length} repositories as unconfirmed`;

      alertApi.post({
        message,
        severity: 'info',
        display: 'transient',
      });

      setLoading(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      alertApi.post({
        message: `Failed to unconfirm ownership: ${errorMessage}`,
        severity: 'error',
      });
      setLoading(false);
      return false;
    }
  };

  return { submit, loading, markAsUnknown };
}
